import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Paper,
  IconButton,
  Grid,
  Switch,
  FormControlLabel,
  MenuItem,
  Divider,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Drawer,
  Chip,
  Badge,
  Skeleton,
  useTheme, 
  useMediaQuery
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import PreviewIcon from "@mui/icons-material/Visibility";
import InfoIcon from "@mui/icons-material/Info";
import RepeatIcon from "@mui/icons-material/Repeat";
import EventIcon from "@mui/icons-material/Event";
import LinkIcon from "@mui/icons-material/Link";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import InboxIcon from "@mui/icons-material/Inbox";
import PendingActionsIcon from "@mui/icons-material/PendingActions";



import { supabase } from "../utils/supabaseClient";

const C = {
  teal:        "#89C7C8",
  tealDark:    "#6fb3b4",
  amber:       "#F5A623",
  amberDark:   "#d4911e",
  coral:       "#E05C5C",
  coralDark:   "#c24a4a",
  violet:      "#9B6DFF",
  violetDark:  "#7c52e0",
  slate:       "#4A5568",
  slateDark:   "#2D3748",
  gray:        "#9e9e9e",
  actionBtn:   "#5E8FA3",
  actionBtnDk: "#4a7389",
};

interface EventEntry {
  id?: string;
  _tempId?: string;
  title: string;
  day: string;
  start_time: string;
  end_time?: string | null;
  cancelled: boolean;
  is_recurring: boolean | string;
  event_date?: string | null;
  override_id?: string;
  recurring_id?: string | null;
  link?: string;
  is_override?: boolean;
}

type PendingChange = {
  type: "update" | "insert";
  event: EventEntry;
  original?: EventEntry;
};

const days = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];

const emptyEvent: EventEntry = {
  title: "",
  day: "Monday",
  start_time: "",
  end_time: "",
  cancelled: false,
  is_recurring: true,
  recurring_id: null,
  link: "",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    "& fieldset": { borderColor: "#e0e0e0" },
    "&:hover fieldset": { borderColor: C.teal },
    "&.Mui-focused fieldset": { borderColor: C.teal },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: C.teal },
};

const toInputTime = (t?: string | null): string => {
  if (!t) return "";
  if (/^\d{2}:\d{2}$/.test(t)) return t;
  const match = t.match(/^(\d{1,2}):(\d{2})(AM|PM)$/i);
  if (!match) return "";
  let hours = parseInt(match[1]);
  const minutes = match[2];
  const meridiem = match[3].toUpperCase();
  if (meridiem === "AM" && hours === 12) hours = 0;
  if (meridiem === "PM" && hours !== 12) hours += 12;
  return `${String(hours).padStart(2, "0")}:${minutes}`;
};

const toInputDate = (d?: string | null): string => {
  if (!d) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  const match = d.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return "";
  return `${match[3]}-${match[1]}-${match[2]}`;
};

const formatDisplayTime = (t?: string | null): string => {
  if (!t) return "";
  if (/^\d{2}:\d{2}$/.test(t)) {
    const [h, m] = t.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const hour = h % 12 || 12;
    return `${hour}:${String(m).padStart(2, "0")} ${ampm}`;
  }
  return t;
};

const getEventBackgroundImage = (title?: string): string | null => {
  if (!title) return null;

  const t = title.toLowerCase();

  if (t.includes("yu-gi-oh") || t.includes("yugioh")) return "/images/yugioh.png";
  if (t.includes("lorcana")) return "/images/lorcana.png";
  if (t.includes("pokemon")) return "/images/pokemon.png";
  if (t.includes("magic") || t.includes("mtg")) return "/images/mtg.png";
  if (t.includes("one piece")) return "/images/onep.png";
  if (t.includes("digimon")) return "/images/digimon.png";
  if (t.includes("union arena")) return "/images/union.png";
  if (t.includes("dragon ball")) return "/images/dragball.png";
  if (t.includes("gundam")) return "/images/gundam.png";

  return null;
};

const normalizeToMMDDYYYY = (dateStr?: string | null): string => {
  if (!dateStr) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [y, m, d] = dateStr.split("-");
    return `${m}/${d}/${y}`;
  }
  return dateStr;
};

const parse24OrAmPm = (t?: string | null): Date | null => {
  if (!t) return null;
  if (/^\d{2}:\d{2}$/.test(t)) {
    const [h, m] = t.split(":").map(Number);
    const d = new Date(); d.setHours(h, m, 0, 0); return d;
  }
  const match = t.match(/^(\d{1,2}):?(\d{2})?(AM|PM)$/i);
  if (!match) return null;
  let hour = parseInt(match[1]);
  const minute = parseInt(match[2] ?? "00");
  const period = match[3].toUpperCase();
  if (period === "PM" && hour !== 12) hour += 12;
  if (period === "AM" && hour === 12) hour = 0;
  const d = new Date(); d.setHours(hour, minute, 0, 0); return d;
};

const fmtTime = (t?: string | null): string => {
  if (!t) return "";
  const d = parse24OrAmPm(t);
  if (!d) return t;
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

const generateRecurringId = (): string =>
  `rec_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const generateEventId = (): string =>
  `rec_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

// ── Skeleton Card ─────────────────────────────────────────────────
const SkeletonCard: React.FC = () => (
  <Paper sx={{ borderRadius: 2, minHeight: 120, overflow: "hidden", display: "flex", flexDirection: "row" }}>
    <Box sx={{ p: 2, flex: 1 }}>
      <Stack direction="row" justifyContent="space-between" mb={1}>
        <Skeleton variant="text" width="55%" height={22} />
        <Skeleton variant="rounded" width={70} height={20} sx={{ borderRadius: 1 }} />
      </Stack>
      <Skeleton variant="text" width="40%" height={16} />
      <Skeleton variant="text" width="30%" height={16} sx={{ mt: 0.5 }} />
    </Box>
    <Box sx={{ width: 64, display: "flex", flexDirection: "column" }}>
      <Skeleton variant="rectangular" width={64} height={60} />
      <Skeleton variant="rectangular" width={64} height={60} />
    </Box>
  </Paper>
);

// ── Empty State ───────────────────────────────────────────────────
const EmptyState: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  actionLabel?: string;
  onAction?: () => void;
  color?: string;
}> = ({ icon, title, subtitle, actionLabel, onAction, color = C.gray }) => (
  <Box sx={{ textAlign: "center", mt: 8, px: 3 }}>
    <Box sx={{
      width: 72, height: 72, borderRadius: "50%",
      bgcolor: `${color}18`,
      display: "flex", alignItems: "center", justifyContent: "center",
      mx: "auto", mb: 2,
    }}>
      <Box sx={{ color, fontSize: 36, display: "flex" }}>{icon}</Box>
    </Box>
    <Typography fontWeight={700} fontSize={16} mb={0.5}>{title}</Typography>
    <Typography color="text.secondary" fontSize={14} mb={2}>{subtitle}</Typography>
    {actionLabel && onAction && (
      <Button
        variant="outlined"
        size="small"
        onClick={onAction}
        sx={{ borderRadius: 2, textTransform: "none", borderColor: color, color }}
      >
        {actionLabel}
      </Button>
    )}
  </Box>
);

// ── Confirm Dialog ────────────────────────────────────────────────
const ConfirmDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel: string;
  confirmColor?: string;
  confirmHover?: string;
  icon?: React.ReactNode;
  iconBg?: string;
  loading?: boolean;
}> = ({ loading = false, open, onClose, onConfirm, title, message, confirmLabel, confirmColor = C.coral, confirmHover = C.coralDark, icon, iconBg }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="xs"
    fullWidth
    PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
  >
    {/* Colored top strip */}
    <Box sx={{ height: 4, bgcolor: confirmColor }} />
    <DialogTitle sx={{ pt: 3, pb: 1 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        {icon && (
          <Box sx={{
            width: 36, height: 36, borderRadius: "50%",
            bgcolor: iconBg ?? `${confirmColor}18`,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <Box sx={{ color: confirmColor, display: "flex", fontSize: 20 }}>{icon}</Box>
          </Box>
        )}
        <Typography fontWeight={700} fontSize={16}>{title}</Typography>
      </Stack>
    </DialogTitle>
    <DialogContent sx={{ pb: 1 }}>
      <Typography color="text.secondary" fontSize={14}>{message}</Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
      <Button
        onClick={onClose}
        disabled={loading}
        sx={{ borderRadius: 2, textTransform: "none", color: "text.secondary" }}
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        onClick={onConfirm}
        disabled={loading}
        sx={{
          borderRadius: 2, textTransform: "none", fontWeight: 600, boxShadow: "none",
          bgcolor: confirmColor, "&:hover": { bgcolor: confirmHover },
          minWidth: 140,
        }}
      >
        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} sx={{ color: "rgba(255,255,255,0.8)" }} />
            <span>Saving...</span>
          </Stack>
        ) : confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
);

// ── Web Preview Day Card ──────────────────────────────────────────
const WebPreviewDayCard: React.FC<{
  day: string;
  dayIndex: number;
  allEvents: EventEntry[];
  pendingChanges: PendingChange[];
  weekOffset: number;
}> = ({ day, dayIndex, allEvents, pendingChanges, weekOffset }) => {
  const now = new Date();
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayIndex = (today.getDay() + 6) % 7;
  const monday = new Date(today);
  monday.setDate(today.getDate() - todayIndex + weekOffset * 7);
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(0, 0, 0, 0);

  const cardDate = new Date(monday);
  cardDate.setDate(monday.getDate() + dayIndex);
  const mm = String(cardDate.getMonth() + 1).padStart(2, "0");
  const dd = String(cardDate.getDate()).padStart(2, "0");
  const yyyy = cardDate.getFullYear();
  const cardDateStr = `${mm}/${dd}/${yyyy}`;
  const isToday = weekOffset === 0 && todayIndex === dayIndex;

  const filterEvents = (): EventEntry[] => {
    return allEvents.flatMap((e) => {
      const isRec = e.is_recurring === true || e.is_recurring === "yes";
      const eventDateNorm = normalizeToMMDDYYYY(e.event_date);

      if (!isRec && eventDateNorm) {
        const [emm, edd, eyyyy] = eventDateNorm.split("/").map(Number);
        if (!emm || !edd || !eyyyy) return [];
        const actualDate = new Date(eyyyy, emm - 1, edd);
        actualDate.setHours(0, 0, 0, 0);
        if (actualDate >= monday && actualDate <= sunday) {
          const actualIndex = (actualDate.getDay() + 6) % 7;
          if (actualIndex === dayIndex) return [e];
        }
        return [];
      }

            if (isRec && e.day === day) {
          const overrideExists = allEvents.some((other) => {
            const otherNorm = normalizeToMMDDYYYY(other.event_date);
            return (
              otherNorm === cardDateStr &&
              !other.is_recurring &&
              other.recurring_id &&
              e.recurring_id &&
              other.recurring_id === e.recurring_id
            );
          });
        if (overrideExists) return [];
        if (e.cancelled && e.event_date) {
          const cancelDateNorm = normalizeToMMDDYYYY(e.event_date);
          if (cancelDateNorm === cardDateStr) return [{ ...e, cancelled: true }];
          return [{ ...e, cancelled: false }];
        }
        return [{ ...e, cancelled: !!e.cancelled }];
      }
      return [];
    });
  };

  const dayEvents = filterEvents();

  const isEventLive = (event: EventEntry): boolean => {
    if (event.cancelled) return false;
    if (todayIndex !== dayIndex || weekOffset !== 0) return false;
    const start = parse24OrAmPm(event.start_time);
    if (!start) return false;
    const end = event.end_time
      ? parse24OrAmPm(event.end_time) ?? new Date(start.getTime() + 4 * 3600000)
      : new Date(start.getTime() + 4 * 3600000);
    return now >= start && now <= end;
  };

  const isPastCard = (): boolean => cardDate.getTime() < today.getTime();

  const getEventDateLabel = (event: EventEntry): string => {
    const norm = normalizeToMMDDYYYY(event.event_date);
    if (!event.is_recurring && norm) {
      const [emm, edd, eyyyy] = norm.split("/").map(Number);
      if (emm && edd && eyyyy) {
        const dt = new Date(eyyyy, emm - 1, edd); dt.setHours(0, 0, 0, 0);
        const formatted = dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
        return dt.getTime() === today.getTime() && weekOffset === 0 ? `Today – ${formatted}` : formatted;
      }
    }
    const eventIndex = days.indexOf(event.day);
    const eventDate = new Date(monday);
    eventDate.setDate(monday.getDate() + eventIndex);
    eventDate.setHours(0, 0, 0, 0);
    const formatted = eventDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return eventDate.getTime() === today.getTime() && weekOffset === 0 ? `Today – ${formatted}` : formatted;
  };

  const isPending = (event: EventEntry): boolean =>
    pendingChanges.some((p) => p.event.id === event.id || (!event.id && p.event.title === event.title));

  return (
    <Box sx={{
      border: isToday ? `2px solid ${C.teal}` : "1px solid #2D2D42",
      bgcolor: isToday ? "#333356" : "#2D2D42",
      borderRadius: 2, p: 2, textAlign: "center",
      transition: "background 0.3s, transform 0.3s",
      "&:hover": { bgcolor: "#3A3A5A", transform: "translateY(-3px)" },
    }}>
      <Typography variant="subtitle1" sx={{ color: "#FFFFFF", fontWeight: 600, mb: 1 }}>{day}</Typography>
      {dayEvents.length > 0 ? (
        <Stack spacing={1} mt={1}>
          {dayEvents.map((event, i) => (
            <Box key={event.id ?? i} sx={{
              bgcolor: "#1E1E2F", borderRadius: 1, p: 1,
              opacity: event.cancelled ? 0.5 : 1,
              outline: isPending(event) ? `1px dashed ${C.violet}` : "none",
            }}>
              <Typography variant="caption" sx={{ color: "#AAAAAA", textAlign: "center", display: "block", mb: 0.5 }}>
                {getEventDateLabel(event)}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography variant="body2" sx={{ color: "#FFFFFF", fontWeight: 500 }}>{event.title}</Typography>
                {event.link && event.link.trim() !== "" && (
                  <IconButton size="small" href={event.link} target="_blank" rel="noopener noreferrer" sx={{ color: C.teal, ml: 1 }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Typography variant="caption" sx={{ color: "#C0C0C0", display: "block", textAlign: "center" }}>
                {event.end_time && event.end_time.trim() !== ""
                  ? `${fmtTime(event.start_time)} – ${fmtTime(event.end_time)}`
                  : `Starts at ${fmtTime(event.start_time)}`}
              </Typography>
              <Stack direction="row" justifyContent="center" spacing={1} mt={0.5}>
                {event.cancelled && <Chip label="Cancelled" color="error" size="small" sx={{ fontWeight: 600 }} />}
                {!event.cancelled && weekOffset === 0 && isEventLive(event) && (
                  <Chip label="LIVE" color="success" size="small" sx={{ fontWeight: 600 }} />
                )}
                {!event.cancelled && weekOffset === 0 && !isEventLive(event) && isPastCard() && (
                  <Chip label="Ended" size="small" sx={{ fontWeight: 600, bgcolor: "#444", color: "#aaa" }} />
                )}
              </Stack>
            </Box>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" sx={{ color: "#555", mt: 1, fontSize: 12 }}>No events</Typography>
      )}
    </Box>
  );
};

// ── Main Component ────────────────────────────────────────────────
const EventEditor = () => {
  const [events, setEvents] = useState<EventEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<EventEntry | null>(null);
  const [pendingChanges, setPendingChanges] = useState<PendingChange[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [webPreviewOpen, setWebPreviewOpen] = useState(false);
  const [previewWeekOffset, setPreviewWeekOffset] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [tab, setTab] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState<EventEntry | null>(null);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [search, setSearch] = useState("");

  const [isPublishing, setIsPublishing] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const fetchEvents = async () => {
    setLoading(true);
    const { data } = await supabase.from("events").select("*");
    setEvents(data || []);
    setLoading(false);
  };

  useEffect(() => { fetchEvents(); }, []);

  const recurring = events.filter((e) => e.is_recurring);
  const oneTime = events.filter((e) => !e.is_recurring);
  const overridableRecurring = events.filter((e) => !!e.is_recurring && !!e.recurring_id);

  const filtered = (tab === 0 ? oneTime : tab === 1 ? recurring : []).filter((e) =>
    e.title.toLowerCase().includes(search.toLowerCase())
  );

  const mergedForPreview = (): EventEntry[] => {
    const base = [...events];
    for (const change of pendingChanges) {
      if (change.type === "insert") { base.push(change.event); }
      else {
        const idx = base.findIndex((e) => e.id === change.event.id);
        if (idx !== -1) base[idx] = change.event; else base.push(change.event);
      }
    }
    return base;
  };

  const openEditor = (e: EventEntry) => {
    const pending = pendingChanges.find((p) => p.event.id === e.id);
    const source = pending ? pending.event : e;
    setEditing({
      ...source,
      start_time: toInputTime(source.start_time),
      end_time: toInputTime(source.end_time),
      event_date: toInputDate(source.event_date),
      is_recurring: !!source.is_recurring,
      link: source.link ?? "",
    });
    setDrawerOpen(true);
  };

  const openNewEditor = (isRecurring: boolean) => {
    setEditing({
      ...emptyEvent,
      is_recurring: isRecurring,
      recurring_id: isRecurring ? generateRecurringId() : null,
    });
    setDrawerOpen(true);
  };

  const closeDrawer = () => { setDrawerOpen(false); setEditing(null); };

  const stageEvent = () => {
    if (!editing) return;
    if (editing.cancelled && editing.is_recurring && !editing.event_date) {
      alert("Please enter a cancellation date for this recurring event.");
      return;
    }
    const isNew = !editing.id;
    const eventToStage = isNew
      ? { ...editing, _tempId: `temp_${Date.now()}_${Math.random().toString(36).slice(2, 5)}` }
      : { ...editing };
    setPendingChanges((prev) => {
      if (isNew) return [...prev, { type: "insert", event: eventToStage }];
      const f = prev.filter((p) => p.event.id !== editing.id);
      const original = events.find((e) => e.id === editing.id);
      return [...f, { type: "update", event: eventToStage, original }];
    });
    closeDrawer();
  };

  const removePending = (index: number) =>
    setPendingChanges((prev) => prev.filter((_, i) => i !== index));

  const to12HourTime = (timeStr?: string | null): string => {
    if (!timeStr) return "";
    if (timeStr.toUpperCase().includes("AM") || timeStr.toUpperCase().includes("PM")) return timeStr;
    const [hourStr, minuteStr] = timeStr.split(":");
    let hour = parseInt(hourStr, 10);
    const minute = minuteStr || "00";
    const period = hour >= 12 ? "PM" : "AM";
    if (hour === 0) hour = 12;
    else if (hour > 12) hour -= 12;
    return `${hour}:${minute}${period}`;
  };

  const publishAll = async () => {
    setIsPublishing(true);
    for (const change of pendingChanges) {
      const { _tempId, ...rawEvent } = change.event as any;
      const eventData = {
        ...rawEvent,
        is_recurring: rawEvent.is_recurring === true || rawEvent.is_recurring === "true" || rawEvent.is_recurring === "True" ? "yes" : null,
        start_time: to12HourTime(rawEvent.start_time),
        end_time: to12HourTime(rawEvent.end_time),
        is_override: !rawEvent.is_recurring && rawEvent.recurring_id ? true : false,
      };
      if (change.type === "insert") {
        await supabase.from("events").insert([{ ...eventData, id: generateEventId() }]);
      } else {
        await supabase.from("events").update(eventData).eq("id", eventData.id!);
      }
    }
    setPendingChanges([]);
    setConfirmPublish(false);
    setIsPublishing(false);
    fetchEvents();
  };

  const deleteEvent = async () => {
    if (!confirmDelete) return;
    await supabase.from("events").delete().eq("id", confirmDelete.id);
    setConfirmDelete(null);
    closeDrawer();
    fetchEvents();
  };

  const previewEvents = mergedForPreview();
  const isNewEvent = editing && !editing.id;
  const drawerAccentColor = editing?.is_recurring ? C.teal : C.amber;
  
  // ✅ REUSABLE FORM CONTENT
const formContent = editing ? (
  <Box
    sx={{
      overflowY: "auto",
      pt: 1,
      ...(isMobile && { flex: 1 }),
    }}
  >
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Title"
          value={editing.title}
          onChange={(e) => setEditing({ ...editing, title: e.target.value })}
          sx={{
            ...fieldSx,
            maxWidth: { sm: 500 },
          }}
        />
      </Grid>

      {/* EVENT INFO LINK — one-time events only */}
      {!editing.is_recurring && (
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Event Info URL (optional)"
            placeholder="https://..."
            value={editing.link ?? ""}
            onChange={(e) => setEditing({ ...editing, link: e.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LinkIcon sx={{ fontSize: 16, color: C.amber }} />
                </InputAdornment>
              ),
            }}
            helperText="Link to event details, registration page, or more info. Shown as an info button on the web schedule."
            sx={{
              ...fieldSx,
              maxWidth: { sm: 500 },
              "& .MuiFormHelperText-root": { color: "#888" },
            }}
          />
        </Grid>
      )}

      {/* TIME ROW */}
      <Grid item xs={12}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label="Start Time"
            type="time"
            value={editing.start_time}
            onChange={(e) =>
              setEditing({ ...editing, start_time: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
            sx={{
              ...fieldSx,
              width: { xs: "100%", sm: 180 },
            }}
          />

          <TextField
            label="End Time"
            type="time"
            value={editing.end_time || ""}
            onChange={(e) =>
              setEditing({ ...editing, end_time: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
            sx={{
              ...fieldSx,
              width: { xs: "100%", sm: 180 },
            }}
          />
        </Stack>
      </Grid>

      {/* RECURRING VS DATE */}
      {editing.is_recurring ? (
        <Grid item xs={12}>
          <TextField
            select
            label="Day of Week"
            value={editing.day}
            onChange={(e) =>
              setEditing({ ...editing, day: e.target.value })
            }
            sx={{
              ...fieldSx,
              width: { xs: "100%", sm: 220 },
            }}
          >
            {days.map((d) => (
              <MenuItem key={d} value={d}>
                {d}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      ) : (
        <Grid item xs={12}>
          <TextField
            label="Event Date"
            type="date"
            value={editing.event_date || ""}
            onChange={(e) => {
              const dateVal = e.target.value;
              let dayOfWeek = editing.day;

              if (dateVal) {
                const [y, m, d] = dateVal.split("-").map(Number);
                const jsDay = new Date(y, m - 1, d).getDay();
                const mapped = [6, 0, 1, 2, 3, 4, 5];
                dayOfWeek = days[mapped[jsDay]];
              }

              setEditing({
                ...editing,
                event_date: dateVal,
                day: dayOfWeek,
              });
            }}
            InputLabelProps={{ shrink: true }}
            sx={{
              ...fieldSx,
              width: { xs: "100%", sm: 220 },
            }}
          />
        </Grid>
      )}

      {/* OVERRIDE */}
      {!editing.is_recurring && overridableRecurring.length > 0 && (
        <Grid item xs={12}>
          <TextField
            select
            label="Replace Recurring Event For the Date (optional)"
            value={editing.recurring_id || ""}
            onChange={(e) => {
              const chosen = overridableRecurring.find(
                (r) => r.recurring_id === e.target.value
              );

              setEditing({
                ...editing,
                recurring_id: e.target.value || null,
                day: chosen?.day || editing.day,
              });
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LinkIcon sx={{ fontSize: 16, color: C.amber }} />
                </InputAdornment>
              ),
            }}
            helperText={
              editing.recurring_id
                ? "This one-time event will replace the selected recurring event on its date."
                : "Link to a recurring event to override it on a specific date."
            }
            sx={{
              ...fieldSx,
              maxWidth: { sm: 500 },
              "& .MuiFormHelperText-root": {
                color: editing.recurring_id ? C.amber : "#888",
              },
            }}
          >
            <MenuItem value="">
              <em style={{ color: "#999" }}>
                None — standalone event
              </em>
            </MenuItem>

            {overridableRecurring.map((r) => (
              <MenuItem key={r.recurring_id} value={r.recurring_id!}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <RepeatIcon sx={{ fontSize: 14, color: C.teal }} />
                  <span>{r.title}</span>
                  <Chip
                    label={r.day}
                    size="small"
                    sx={{
                      fontSize: 10,
                      height: 18,
                      bgcolor: "#eee",
                    }}
                  />
                </Stack>
              </MenuItem>
            ))}
          </TextField>

          {editing.recurring_id && (
            <Box
              sx={{
                mt: 1,
                p: 1.5,
                bgcolor: "#fff8ee",
                borderRadius: 1,
                border: `1px solid ${C.amber}`,
                maxWidth: { sm: 500 },
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: C.amberDark, fontWeight: 600 }}
              >
                🔁 Override active — this event will replace the recurring event
                on the selected date.
              </Typography>
            </Box>
          )}
        </Grid>
      )}

      {/* CANCEL SECTION */}
      {!isNewEvent && (
        <>
          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={editing.cancelled}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      cancelled: e.target.checked,
                      event_date:
                        !e.target.checked && editing.is_recurring
                          ? null
                          : editing.event_date,
                    })
                  }
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: C.coral,
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      bgcolor: C.coral,
                    },
                  }}
                />
              }
              label="Cancelled"
            />
          </Grid>

          {editing.cancelled && editing.is_recurring && (
            <Grid item xs={12}>
              <TextField
                required
                label="Cancellation Date"
                type="date"
                value={editing.event_date || ""}
                onChange={(e) =>
                  setEditing({ ...editing, event_date: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                helperText={
                  !editing.event_date
                    ? "Required — which date is this event cancelled for?"
                    : "This specific date will be marked cancelled"
                }
                error={!editing.event_date}
                sx={{
                  ...fieldSx,
                  width: { xs: "100%", sm: 220 },
                  "& .MuiFormHelperText-root": {
                    color: !editing.event_date ? C.coral : "#888",
                  },
                }}
              />
            </Grid>
          )}
        </>
      )}
    </Grid>
  </Box>
) : null;


// ✅ HEADER (used in drawer only)
const headerContent = editing ? (
  <>
    <Box sx={{ height: 4, bgcolor: drawerAccentColor, borderRadius: "4px 4px 0 0" }} />

    {isMobile && (
      <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
        <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: "#e0e0e0" }} />
      </Box>
    )}

    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
      <Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6" fontWeight={700}>
            {isNewEvent ? "New Event" : "Edit Event"}
          </Typography>

          <Chip
            label={editing.is_recurring ? "Recurring" : "One-Time"}
            size="small"
            sx={{ bgcolor: drawerAccentColor, color: "#fff", fontWeight: 600 }}
          />
        </Stack>

        <Typography variant="body2" color="text.secondary">
          Changes are queued until you publish them.
        </Typography>
      </Box>

      <IconButton onClick={closeDrawer}>
        <CloseIcon />
      </IconButton>
    </Stack>
  </>
) : null;


// ✅ ACTION BUTTONS (shared)
const actionButtons = (
  <Stack direction="row" spacing={2} sx={{ justifyContent: { xs: "stretch", md: "flex-start" } }}>
    <Button
      variant="contained"
      onClick={stageEvent}
      size="large"
      sx={{
        textTransform: "none",
        borderRadius: 2,
        fontWeight: 600,
        bgcolor: C.actionBtn,
        "&:hover": { bgcolor: C.actionBtnDk },
        boxShadow: "none",
        width: { xs: "100%", md: "auto" },
        flex: { xs: 1, md: "none" },
      }}
    >
      Add to Queue
    </Button>

    {!isNewEvent && editing?.id && (
      <Button
        startIcon={<DeleteIcon />}
        variant="outlined"
        onClick={() => setConfirmDelete(editing)}
        size="large"
        sx={{
          textTransform: "none",
          borderRadius: 2,
          fontWeight: 600,
          borderColor: C.coral,
          color: C.coral,
          "&:hover": { bgcolor: "#fdecea", borderColor: C.coralDark },
          width: { xs: "100%", md: "auto" },
          flex: { xs: 1, md: "none" },
        }}
      >
        Delete
      </Button>
    )}
  </Stack>
);  

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>

      {/* STICKY TOP SECTION */}
      <Box sx={{ flexShrink: 0, pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} gap={2}>
          <TextField
            placeholder="Search events..."
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ ...fieldSx, flex: 1, minWidth: 0 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          />
          <Button
            startIcon={<PreviewIcon />}
            variant="outlined"
            size="small"
            onClick={() => { setPreviewWeekOffset(0); setWebPreviewOpen(true); }}
            sx={{
              textTransform: "none", borderRadius: 2, borderColor: C.teal, color: C.teal,
              whiteSpace: "nowrap", flexShrink: 0,
              "&:hover": { bgcolor: "#e0f7f7", borderColor: C.tealDark },
            }}
          >
            Web Preview
          </Button>
        </Stack>

        {/* TABS with count badges and + icons */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            mb: 2,
            "& .MuiTabs-indicator": { display: "none" },
            "& .MuiTab-root": {
              borderRadius: "8px 8px 0 0",
              minHeight: 40,
              fontWeight: 600,
              textTransform: "none",
              fontSize: 13,
              transition: "background 0.2s, color 0.2s",
              color: "#888",
              mx: 0.25,
            },
            "& .MuiTab-root:nth-of-type(1)": {
              bgcolor: "#fff3e0",
              "&.Mui-selected": { bgcolor: C.amber, color: "#fff" },
            },
            "& .MuiTab-root:nth-of-type(2)": {
              bgcolor: "#e0f7f7",
              "&.Mui-selected": { bgcolor: C.teal, color: "#fff" },
            },
            "& .MuiTab-root:nth-of-type(3)": {
              bgcolor: "#f0ebff",
              "&.Mui-selected": { bgcolor: C.violet, color: "#fff" },
            },
          }}
        >
          <Tab
            label={
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <span>One-Time</span>
                <Chip
                  label={oneTime.length}
                  size="small"
                  sx={{
                    height: 16, fontSize: 10, fontWeight: 700,
                    bgcolor: tab === 0 ? "rgba(255,255,255,0.3)" : "rgba(245,166,35,0.2)",
                    color: tab === 0 ? "#fff" : C.amber,
                    "& .MuiChip-label": { px: 0.75 },
                  }}
                />
                <Box
                  onClick={(ev) => { ev.stopPropagation(); openNewEditor(false); }}
                  sx={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    width: 18, height: 18, borderRadius: "50%",
                    bgcolor: tab === 0 ? "rgba(255,255,255,0.3)" : "rgba(245,166,35,0.2)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.5)" },
                  }}
                >
                  <AddIcon sx={{ fontSize: 13 }} />
                </Box>
              </Stack>
            }
          />
          <Tab
            label={
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <span>Recurring</span>
                <Chip
                  label={recurring.length}
                  size="small"
                  sx={{
                    height: 16, fontSize: 10, fontWeight: 700,
                    bgcolor: tab === 1 ? "rgba(255,255,255,0.3)" : "rgba(137,199,200,0.2)",
                    color: tab === 1 ? "#fff" : C.teal,
                    "& .MuiChip-label": { px: 0.75 },
                  }}
                />
                <Box
                  onClick={(ev) => { ev.stopPropagation(); openNewEditor(true); }}
                  sx={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    width: 18, height: 18, borderRadius: "50%",
                    bgcolor: tab === 1 ? "rgba(255,255,255,0.3)" : "rgba(137,199,200,0.2)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.5)" },
                  }}
                >
                  <AddIcon sx={{ fontSize: 13 }} />
                </Box>
              </Stack>
            }
          />
          <Tab
            label={
              <Badge badgeContent={pendingChanges.length} color="error" invisible={pendingChanges.length === 0}>
                <Box sx={{ pr: pendingChanges.length > 0 ? 1.5 : 0 }}>Pending Changes</Box>
              </Badge>
            }
          />
        </Tabs>

        {tab === 2 && pendingChanges.length > 0 && (
          <Stack direction="row" justifyContent="flex-end" mb={2}>
            <Button
              startIcon={<SaveIcon />}
              variant="contained"
              sx={{
                borderRadius: 2, textTransform: "none", fontWeight: 600,
                bgcolor: C.violet, "&:hover": { bgcolor: C.violetDark }, boxShadow: "none",
              }}
              onClick={() => setConfirmPublish(true)}
            >
              Save All Changes ({pendingChanges.length})
            </Button>
          </Stack>
        )}
      </Box>

      {/* SCROLLABLE CONTENT */}
      <Box sx={{ overflowY: "auto", flex: 1, pr: 0.5 }}>

        {tab !== 2 && (
          <>
            {/* SKELETON LOADING */}
            {loading && (
              <Grid container spacing={2}>
                {[...Array(6)].map((_, i) => (
                  <Grid key={i} item xs={12} sm={6} md={4} lg={3}>
                    <SkeletonCard />
                  </Grid>
                ))}
              </Grid>
            )}

            {/* EMPTY STATE */}
            {!loading && filtered.length === 0 && (
              <EmptyState
                icon={tab === 0 ? <CalendarTodayIcon fontSize="inherit" /> : <RepeatIcon fontSize="inherit" />}
                color={tab === 0 ? C.amber : C.teal}
                title={search ? "No results found" : tab === 0 ? "No one-time events yet" : "No recurring events yet"}
                subtitle={
                  search
                    ? `No events match "${search}". Try a different search.`
                    : tab === 0
                    ? "Add a one-time event by clicking the + in the One-Time tab."
                    : "Add a recurring event by clicking the + in the Recurring tab."
                }
                actionLabel={search ? "Clear search" : undefined}
                onAction={search ? () => setSearch("") : undefined}
              />
            )}

{/* EVENT CARDS */}
{!loading && filtered.length > 0 && (
  <Grid container spacing={2}>
    {filtered.map((e) => {
      const isCancelled = e.cancelled;
      const isRecurring = e.is_recurring;
      const hasPending = pendingChanges.some((p) => p.event.id === e.id);

      const isPast = (() => {
        if (isRecurring || !e.event_date) return false;
        const norm = normalizeToMMDDYYYY(e.event_date);
        const [mm, dd, yyyy] = norm.split("/").map(Number);
        if (!mm || !dd || !yyyy) return false;
        const eventDate = new Date(yyyy, mm - 1, dd);
        eventDate.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return eventDate.getTime() < today.getTime();
      })();

      // ✅ BACKGROUND IMAGE LOGIC
      const getEventBackgroundImage = (title?: string): string | null => {
        if (!title) return null;
        const t = title.toLowerCase();

        if (t.includes("yu-gi-oh") || t.includes("yugioh")) return "/images/yugioh.png";
        if (t.includes("lorcana")) return "/images/lorcana.png";
        if (t.includes("pokemon")) return "/images/pokemon.png";
        if (t.includes("magic") || t.includes("mtg")) return "/images/mtg.png";
        if (t.includes("one piece")) return "/images/onep-b.png";
        if (t.includes("digimon")) return "/images/digimon.png";
        if (t.includes("union arena")) return "/images/union.png";
        if (t.includes("dragon ball")) return "/images/dragball.png";
        if (t.includes("gundam")) return "/images/gundam.png";

        return null;
      };

      const bgImage = getEventBackgroundImage(e.title);

      return (
        <Grid key={e.id} item xs={12} sm={6} md={4} lg={3}>
          <Paper
            sx={{
              position: "relative",
              borderRadius: 2,
              minHeight: 120,
              overflow: "hidden",
              borderLeft: isPast
                ? `4px solid ${C.gray}`
                : isRecurring
                ? `4px solid ${C.teal}`
                : `4px solid ${C.amber}`,
              opacity: isPast ? 0.6 : isCancelled ? 0.5 : 1,
              textDecoration: isCancelled ? "line-through" : "none",
              transition: "all 0.2s",
              display: "flex",
              flexDirection: "row",
              outline: hasPending ? `2px solid ${C.violet}` : "none",
              bgcolor: isPast ? "#fafafa" : "background.paper",

              // ✅ BACKGROUND IMAGE
              ...(bgImage && {
                backgroundImage: `url(${bgImage})`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right center",
                backgroundSize: "170px",
              }),

              "&:hover": { boxShadow: 4 },
            }}
          >
            {/* ✅ OVERLAY (gradient for better look) */}
            {bgImage && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
  "linear-gradient(to right, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.75) 50%, rgba(0,0,0,0.25) 100%)",
                  pointerEvents: "none",
                }}
              />
            )}

            <Box
              onClick={() =>
                setExpandedId(expandedId === e.id ? null : e.id ?? null)
              }
              sx={{ p: 2, flex: 1, cursor: "pointer", position: "relative" }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                mb={0.5}
              >
                <Stack direction="column" spacing={0.25} sx={{ flex: 1, pr: 1 }}>
                  <Typography
                    fontWeight={600}
                    sx={{ color: isPast ? C.gray : "inherit" }}
                  >
                    {e.title}
                  </Typography>

                  {isPast && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#bdbdbd",
                        fontStyle: "italic",
                        fontSize: 10,
                      }}
                    >
                      🗓 Past event — consider deleting
                    </Typography>
                  )}
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  {hasPending && (
                    <Chip
                      label="Pending"
                      size="small"
                      sx={{
                        bgcolor: C.violet,
                        color: "#fff",
                        fontSize: 10,
                        height: 20,
                      }}
                    />
                  )}

                  {isPast ? (
                    <Box
                      sx={{
                        bgcolor: C.gray,
                        color: "#fff",
                        px: 1.5,
                        py: 0.25,
                        borderRadius: 1,
                        fontSize: 12,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      Past
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        bgcolor: isRecurring ? C.teal : C.amber,
                        color: "#fff",
                        px: 1.5,
                        py: 0.25,
                        borderRadius: 1,
                        fontSize: 12,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      {isRecurring ? "Recurring" : "One-Time"}
                    </Box>
                  )}
                </Stack>
              </Stack>

              <Typography
                variant="body2"
                sx={{ color: isPast ? "#bdbdbd" : "text.secondary" }}
              >
                {isRecurring
                  ? `${e.day} • ${e.start_time}`
                  : `${e.event_date} • ${e.start_time}`}
                {e.override_id && " • Overrides"}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 0,
                width: expandedId === e.id ? 64 : 0,
                overflow: "hidden",
                transition: "width 0.25s ease",
              }}
            >
              <IconButton
                size="small"
                onClick={() => {
                  openEditor(e);
                  setExpandedId(null);
                }}
                sx={{
                  color: "#fff",
                  bgcolor: C.teal,
                  borderRadius: 0,
                  width: "100%",
                  height: 60,
                  "&:hover": { bgcolor: C.tealDark },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>

              <IconButton
                size="small"
                onClick={() => {
                  setConfirmDelete(e);
                  setExpandedId(null);
                }}
                sx={{
                  color: "#fff",
                  bgcolor: C.coral,
                  borderRadius: 0,
                  width: "100%",
                  height: 60,
                  "&:hover": { bgcolor: C.coralDark },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        </Grid>
      );
    })}
  </Grid>
)}
          </>
        )}

        {tab === 2 && (
          <Box>
            {pendingChanges.length === 0 ? (
              <EmptyState
                icon={<PendingActionsIcon fontSize="inherit" />}
                color={C.violet}
                title="Your queue is empty"
                subtitle="Edit or add events and they'll appear here before publishing."
              />
            ) : (
              <Stack spacing={2}>
                {pendingChanges.map((change, index) => {
                  const e = change.event;
                  const isNew = change.type === "insert";
                  const isRecurring = e.is_recurring;
                  return (
                    <Paper key={index} sx={{
                      borderRadius: 2, overflow: "hidden",
                      borderLeft: `4px solid ${C.violet}`,
                      display: "flex", flexDirection: "row",
                    }}>
                      <Box sx={{ p: 2, flex: 1 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography fontWeight={700}>{e.title || "Untitled"}</Typography>
                            <Chip label={isNew ? "New" : "Updated"} size="small"
                              sx={{ bgcolor: isNew ? "#e8f5e9" : undefined, color: isNew ? "#2e7d32" : C.slateDark, fontWeight: 600, fontSize: 11 }} />
                            {e.cancelled && <Chip label="Cancelled" size="small" color="error" />}
                          </Stack>
                          <Box sx={{ bgcolor: isRecurring ? C.teal : C.amber, color: "#fff", px: 1.5, py: 0.25, borderRadius: 1, fontSize: 12, fontWeight: 600 }}>
                            {isRecurring ? "Recurring" : "One-Time"}
                          </Box>
                        </Stack>
                        <Stack spacing={0.5}>
                          {isRecurring
                            ? <Typography variant="body2" color="text.secondary">📅 {e.day}</Typography>
                            : <Typography variant="body2" color="text.secondary">📅 {e.event_date || "No date"}</Typography>}
                          <Typography variant="body2" color="text.secondary">
                            🕐 {formatDisplayTime(e.start_time) || "No start time"}
                            {e.end_time ? ` – ${formatDisplayTime(e.end_time)}` : ""}
                          </Typography>
                          {e.cancelled && e.is_recurring && e.event_date && (
                            <Typography variant="body2" sx={{ color: C.coral }}>
                              ❌ Cancelled for: {e.event_date}
                            </Typography>
                          )}
                          {e.link && e.link.trim() !== "" && (
                            <Typography variant="body2" color="text.secondary">
                              🔗{" "}
                              <a href={e.link} target="_blank" rel="noopener noreferrer" style={{ color: C.amber }}>
                                {e.link.length > 50 ? e.link.slice(0, 50) + "…" : e.link}
                              </a>
                            </Typography>
                          )}
                        </Stack>
                        {change.original && (
                          <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>BEFORE:</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {change.original.title} • {change.original.is_recurring
                                ? `${change.original.day} • ${change.original.start_time}`
                                : `${change.original.event_date} • ${change.original.start_time}`}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", px: 1.5, bgcolor: "#f5f5f5", cursor: "pointer", "&:hover": { bgcolor: "#ffebee" }, transition: "background 0.2s" }}
                        onClick={() => removePending(index)}>
                        <UndoIcon sx={{ color: C.coral, fontSize: 20 }} />
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Box>
        )}
      </Box>

{editing && (
  isMobile ? (
    // 📱 MOBILE DRAWER
    <Drawer
      anchor="bottom"
      open={drawerOpen}
      onClose={closeDrawer}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          height: "70vh",
          px: 2,
          pt: 0,
          pb: 4,
          overflow: "hidden",
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {headerContent}
        {formContent}
        <Divider sx={{ my: 2 }} />
        {actionButtons}
      </Box>
    </Drawer>
  ) : (
    // 💻 DESKTOP DIALOG
    <Dialog
      open={drawerOpen}
      onClose={closeDrawer}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 2,
          maxHeight: "85vh",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography fontWeight={700} fontSize={18}>
            {isNewEvent ? "New Event" : "Edit Event"}
          </Typography>
          <IconButton onClick={closeDrawer} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {formContent}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {actionButtons}
      </DialogActions>
    </Dialog>
  )
)}

      {/* WEB PREVIEW DIALOG */}
      <Dialog open={webPreviewOpen} onClose={() => setWebPreviewOpen(false)} maxWidth="lg" fullWidth
        PaperProps={{ sx: { bgcolor: "#1E1E2F", borderRadius: 3, p: 1 } }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography fontWeight={700} fontSize={18} color="#e0e0e0">Web Preview</Typography>
              {pendingChanges.length > 0 && (
                <Chip label={`${pendingChanges.length} unsaved`} size="small" sx={{ bgcolor: C.violet, color: "#fff" }} />
              )}
            </Stack>
            <IconButton onClick={() => setWebPreviewOpen(false)} sx={{ color: "#e0e0e0" }}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
            {previewWeekOffset === 1 && (
              <Chip
                label={<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill={C.teal}><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
                  <span style={{ fontSize: "0.75rem", color: C.teal }}>This Week</span>
                </Box>}
                onClick={() => setPreviewWeekOffset(0)}
                variant="outlined"
                sx={{ borderColor: C.teal, fontWeight: 600, px: 1.5, height: 28, cursor: "pointer" }}
              />
            )}
            <Box sx={{ flexGrow: 1 }} />
            {previewWeekOffset === 0 && (
              <Chip
                label={<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <span style={{ fontSize: "0.75rem", color: C.teal }}>Next Week</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill={C.teal}><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
                </Box>}
                onClick={() => setPreviewWeekOffset(1)}
                variant="outlined"
                sx={{ borderColor: C.teal, fontWeight: 600, px: 1.5, height: 28, cursor: "pointer" }}
              />
            )}
          </Box>
          <Grid container spacing={2} justifyContent="center">
            {days.map((day, index) => (
              <Grid item xs={12} sm={6} md={4} key={day}>
                <WebPreviewDayCard day={day} dayIndex={index} allEvents={previewEvents} pendingChanges={pendingChanges} weekOffset={previewWeekOffset} />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Typography variant="caption" color="#888" sx={{ flex: 1 }}>
            ⚠️ Preview only — changes are not live until you go to <strong style={{ color: C.violet }}>Pending Changes</strong> and click <strong>Save All Changes</strong>.
          </Typography>
          <Button onClick={() => setWebPreviewOpen(false)} sx={{ color: C.teal, textTransform: "none" }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* PUBLISH CONFIRM */}
      <ConfirmDialog
        open={confirmPublish}
        onClose={() => setConfirmPublish(false)}
        onConfirm={publishAll}
        title="Save All Changes?"
        message={`This will permanently save ${pendingChanges.length} change${pendingChanges.length > 1 ? "s" : ""} to the database. This cannot be undone.`}
        confirmLabel="Confirm & Save All"
        confirmColor={C.violet}
        confirmHover={C.violetDark}
        icon={<SaveIcon fontSize="inherit" />}
        loading={isPublishing}   // ← add this
      />

      {/* DELETE CONFIRM */}
      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={deleteEvent}
        title="Delete Event?"
        message={confirmDelete?.is_recurring
          ? "This is a recurring event and a core schedule item. Are you sure you want to delete it?"
          : "Are you sure you want to delete this event? This cannot be undone."}
        confirmLabel="Delete"
        confirmColor={C.coral}
        confirmHover={C.coralDark}
        icon={<DeleteIcon fontSize="inherit" />}
      />
    </Box>
  );
};

export default EventEditor;